import { createRouter, createWebHistory } from 'vue-router'
import { getAccessToken } from '@/utils/token'
import { usePostHog } from '@/composables/usePostHog'

// Layouts
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import SidebarLayout from '@/layouts/SidebarLayout.vue'

// Pages - using dynamic imports to avoid case sensitivity issues

// Products - using dynamic imports to avoid case sensitivity issues

// Pharmacies - using dynamic imports to avoid case sensitivity issues

// Stock Requests - using dynamic imports to avoid case sensitivity issues

// Users - using dynamic imports to avoid case sensitivity issues

// Prescriptions - using dynamic imports to avoid case sensitivity issues

// Account, Notifications, CSV Upload, API Documentation - using dynamic imports to avoid case sensitivity issues

const routes = [
  // Public routes (no authentication required)
  {
    path: '/',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: () => import('@/views/LoginPage.vue'),
        meta: { requiresGuest: true }
      },
      {
        path: '/login',
        name: 'LoginPage',
        component: () => import('@/views/LoginPage.vue'),
        meta: { requiresGuest: true }
      },
      {
        path: '/register',
        name: 'Register',
        component: () => import('@/views/RegisterPage.vue'),
        meta: { requiresGuest: true }
      },
      {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPasswordPage.vue'),
        meta: { requiresGuest: true }
      },
      {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/ResetPasswordPage.vue'),
        meta: { requiresGuest: true }
      },
      {
        path: '/email-verification',
        name: 'EmailVerification',
        component: () => import('@/views/EmailVerificationPage.vue')
      }
    ]
  },

  // Protected routes (authentication required)
  {
    path: '/dashboard',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/DashboardPage.vue')
      }
    ]
  },

  // Pharmacy-specific routes (when pharmacy is assigned)
  {
    path: '/pharmacies/:pharmacyId',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: 'dashboard',
        name: 'PharmacyDashboard',
        component: () => import('@/views/DashboardPage.vue')
      },
      {
        path: 'products',
        name: 'PharmacyProductList',
        component: () => import('@/views/products/PharmacyProductListPage.vue')
      },
      {
        path: 'products/create',
        name: 'CreatePharmacyProduct',
        component: () => import('@/views/products/CreatePharmacyProductPage.vue')
      },
      {
        path: 'products/:id',
        name: 'PharmacyProductDetail',
        component: () => import('@/views/products/PharmacyProductDetailPage.vue')
      },
      {
        path: 'products/:id/edit',
        name: 'PharmacyEditProduct',
        component: () => import('@/views/products/EditPharmacyProductPage.vue')
      },
      {
        path: 'products/:productId/variants',
        name: 'PharmacyProductVariants',
        component: () => import('@/views/products/ProductVariantsPage.vue')
      },
      {
        path: 'products/upload',
        name: 'PharmacyProductCSVUpload',
        component: () => import('@/views/products/PharmacyProductCSVUploadPage.vue')
      },
      {
        path: 'products/upload-review/:sessionId',
        name: 'PharmacyProductUploadReview',
        component: () => import('@/views/products/PharmacyProductUploadReviewPage.vue')
      },
      {
        path: 'products/unlinked-review',
        name: 'UnlinkedProductsReview',
        component: () => import('@/views/products/UnlinkedProductsReviewPage.vue')
      },

      {
        path: 'requests',
        name: 'PharmacyRequestsList',
        component: () => import('@/views/Requests/RequestsListPage.vue')
      },
      {
        path: 'requests/create',
        name: 'PharmacyCreateRequest',
        component: () => import('@/views/Requests/CreateRequestPage.vue')
      },
      {
        path: 'requests/search',
        name: 'PharmacySearchProducts',
        component: () => import('@/views/Requests/ProductSearchForRequestsPage.vue')
      },
      {
        path: 'requests/:id',
        name: 'PharmacyRequestDetail',
        component: () => import('@/views/Requests/RequestDetailPage.vue')
      },
      {
        path: 'prescriptions',
        name: 'PharmacyPrescriptionList',
        component: () => import('@/views/Prescriptions/PrescriptionListPage.vue')
      },
      {
        path: 'prescriptions/create',
        name: 'PharmacyCreatePrescription',
        component: () => import('@/views/Prescriptions/PrescriptionCreatePage.vue')
      },
      {
        path: 'prescriptions/show/:id',
        name: 'PharmacyPrescriptionDetail',
        component: () => import('@/views/Prescriptions/PrescriptionDetailPage.vue')
      },
      {
        path: 'users',
        name: 'PharmacyUserList',
        component: () => import('@/views/users/PharmacyUserListPage.vue')
      },
      {
        path: 'users/create',
        name: 'CreatePharmacyUser',
        component: () => import('@/views/users/PharmacyUserCreatePage.vue')
      },
      {
        path: 'users/:id',
        name: 'PharmacyUserDetail',
        component: () => import('@/views/users/UserDetailPage.vue')
      },
      {
        path: 'users/:id/edit',
        name: 'EditPharmacyUser',
        component: () => import('@/views/users/EditUserPage.vue')
      }
    ]
  },

  // Products (Admin routes - when no pharmacy assigned)
  {
    path: '/products',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'ProductList',
        component: () => import('@/views/products/ProductListPage.vue')
      },
      {
        path: 'create',
        name: 'CreateProduct',
        component: () => import('@/views/products/CreateProductPage.vue')
      },
      {
        path: 'upload',
        name: 'ProductCSVUpload',
        component: () => import('@/views/products/ProductCSVUploadPage.vue')
      },
      {
        path: ':id',
        name: 'ProductDetail',
        component: () => import('@/views/products/ProductDetailPage.vue')
      },
      {
        path: ':id/edit',
        name: 'EditProduct',
        component: () => import('@/views/products/EditProductPage.vue')
      },
      {
        path: ':productId/variants',
        name: 'ProductVariants',
        component: () => import('@/views/products/ProductVariantsPage.vue')
      }
    ]
  },

  // Pharmacies
  {
    path: '/pharmacies',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'PharmacyList',
        component: () => import('@/views/pharmacies/PharmacyListPage.vue')
      },
      {
        path: 'show/:id',
        name: 'PharmacyDetail',
        component: () => import('@/views/pharmacies/PharmacyDetailPage.vue')
      },
      {
        path: 'create',
        name: 'CreatePharmacy',
        component: () => import('@/views/pharmacies/CreatePharmacyPage.vue')
      },
      {
        path: 'edit/:id',
        name: 'EditPharmacy',
        component: () => import('@/views/pharmacies/EditPharmacyPage.vue')
      }
    ]
  },

  // Stock Requests
  {
    path: '/requests',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'RequestsList',
        component: () => import('@/views/Requests/RequestsListPage.vue')
      },
      {
        path: 'create',
        name: 'CreateRequest',
        component: () => import('@/views/Requests/CreateRequestPage.vue')
      },
      {
        path: 'search',
        name: 'SearchProducts',
        component: () => import('@/views/Requests/ProductSearchForRequestsPage.vue')
      },
      {
        path: 'show/:id',
        name: 'RequestDetail',
        component: () => import('@/views/Requests/RequestDetailPage.vue')
      }
    ]
  },

  // Users
  {
    path: '/users',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'UserList',
        component: () => import('@/views/users/UserListPage.vue')
      },
      {
        path: 'create',
        name: 'CreateUser',
        component: () => import('@/views/users/CreateUserPage.vue')
      },
      {
        path: 'show/:id',
        name: 'UserDetail',
        component: () => import('@/views/users/UserDetailPage.vue')
      },
      {
        path: 'edit/:id',
        name: 'EditUser',
        component: () => import('@/views/users/EditUserPage.vue')
      }
    ]
  },

  // CSV Upload
  {
    path: '/upload',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'CSVUpload',
        component: () => import('@/views/CSVUploadPage.vue')
      }
    ]
  },

  // Account Management
  {
    path: '/account',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'Account',
        component: () => import('@/views/AccountPage.vue')
      }
    ]
  },

  // Notifications
  {
    path: '/notifications',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: '',
        name: 'Notifications',
        component: () => import('@/views/NotificationsPage.vue')
      }
    ]
  },



  // API Development
  {
    path: '/developers',
    component: SidebarLayout,
    meta: { requiresAuth: true, layout: 'sidebar' },
    children: [
      {
        path: 'api-documentation',
        name: 'APIDocumentation',
        component: () => import('@/views/Developers/APIDocumentationPage.vue')
      },
      {
        path: 'api-tokens',
        name: 'APITokens',
        component: () => import('@/views/Developers/APITokensPage.vue')
      },
      {
        path: 'graphql-playground',
        name: 'GraphQLPlayground',
        component: () => import('@/views/Developers/GraphQLPlaygroundPage.vue')
      }
    ]
  },

  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/dashboard'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Initialize PostHog
const { posthog } = usePostHog()

// Navigation guards
router.beforeEach((to, from, next) => {
  // Track page views with PostHog
  if (posthog && to.name) {
    posthog.capture('$pageview', {
      $current_url: to.fullPath,
      page: to.name
    })
  }
  const token = getAccessToken()
  const isAuthenticated = !!token

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      next('/login')
      return
    }
  }

  // Check if route requires guest (user not logged in)
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (isAuthenticated) {
      next('/dashboard')
      return
    }
  }

  // Check dashboard route - redirect to pharmacy-specific dashboard if pharmacy is assigned
  if (to.path === '/dashboard') {
    const user = JSON.parse(localStorage.getItem('user') || '{}')

    if (user.pharmacy_id) {
      next(`/pharmacies/${user.pharmacy_id}/dashboard`)
      return
    }
  }

  // Check products route - redirect to pharmacy-specific products if pharmacy is assigned
  if (to.path === '/products' || to.path.startsWith('/products/')) {
    const user = JSON.parse(localStorage.getItem('user') || '{}')

    if (user.pharmacy_id) {
      // Redirect to pharmacy-specific products
      const pharmacyProductsPath = `/pharmacies/${user.pharmacy_id}/products`
      
      if (to.path === '/products') {
        next(pharmacyProductsPath)
        return
      } else {
        // Handle nested product routes
        const productSubPath = to.path.replace('/products', '')
        next(`${pharmacyProductsPath}${productSubPath}`)
        return
      }
    }
  }

  next()
})

export default router
